import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Page, formatBRL } from "./_shared";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Area, AreaChart, Legend } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, Package2, ChevronsUpDown, Check, Search } from "lucide-react";

export default function Forecast() {
  const [products, setProducts] = useState([]);
  const [pid, setPid] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => {
      setProducts(r.data);
      if (r.data.length) setPid(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!pid) return;
    setForecast(null);
    api.get(`/products/${pid}/forecast`).then((r) => setForecast(r.data));
  }, [pid]);

  const selected = useMemo(
    () => products.find((p) => p.id === pid),
    [products, pid]
  );

  const chartData = forecast
    ? [
        ...forecast.history_series.map((v, i) => ({ day: `H-${30 - i}`, valor: v, tipo: "Histórico" })),
        ...forecast.forecast_series.map((v, i) => ({ day: `D+${i + 1}`, prev: v, tipo: "Previsão" })),
      ]
    : [];

  return (
    <Page
      title="Previsão de demanda"
      subtitle="Veja quanto cada produto vai vender nos próximos 30 dias com base em IA."
      action={
        <div className="w-full sm:w-80">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                data-testid="forecast-product-trigger"
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between h-11 font-normal touch-manipulation"
              >
                <span className="flex items-center gap-2 truncate">
                  <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="truncate">
                    {selected ? selected.name : "Buscar produto..."}
                  </span>
                </span>
                <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0 bg-card border-border"
              align="end"
            >
              <Command data-testid="forecast-product-command" shouldFilter={true}>
                <CommandInput
                  data-testid="forecast-product-search-input"
                  placeholder="Digite o nome ou SKU..."
                  className="h-10"
                />
                <CommandList className="max-h-72">
                  <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
                  <CommandGroup>
                    {products.map((p) => (
                      <CommandItem
                        key={p.id}
                        value={`${p.name} ${p.sku}`}
                        data-testid={`forecast-product-option-${p.id}`}
                        onSelect={() => {
                          setPid(p.id);
                          setOpen(false);
                        }}
                        className="cursor-pointer touch-manipulation"
                      >
                        <Check
                          className={`w-4 h-4 mr-2 ${pid === p.id ? "opacity-100 text-primary" : "opacity-0"}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{p.name}</div>
                          <div className="text-xs text-muted-foreground">SKU {p.sku}</div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      }
    >
      {!forecast ? (
        <Skeleton className="h-96" />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            <StatBlock icon={Package2} label="Estoque atual" value={`${forecast.stock} un`} />
            <StatBlock icon={TrendingUp} label="Venda média/dia" value={`${forecast.daily_avg_demand} un`} />
            <StatBlock icon={forecast.trend >= 0 ? TrendingUp : TrendingDown} label="Tendência" value={`${forecast.trend > 0 ? "+" : ""}${forecast.trend} un/dia`} accent={forecast.trend !== 0} />
            <StatBlock label="Demanda 30d projetada" value={`${forecast.expected_demand_30d} un`} accent />
          </div>

          <div className="p-6 rounded-xl border border-border bg-card mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">{forecast.name}</h3>
              <span className="text-xs text-muted-foreground">SKU {forecast.sku}</span>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="hist" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#94A3B8" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#94A3B8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="prev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F05A3F" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#F05A3F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1A2B47" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#94A3B8" fontSize={10} tickLine={false} interval={3} />
                <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: "#0B1320", border: "1px solid #1A2B47", borderRadius: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" name="Histórico" dataKey="valor" stroke="#94A3B8" fill="url(#hist)" strokeWidth={2} />
                <Area type="monotone" name="Previsão IA" dataKey="prev" stroke="#F05A3F" fill="url(#prev)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
            <Insight title="Risco de ruptura" value={forecast.stockout_risk ? "SIM" : "Não"} hint={`Ruptura em ~${forecast.days_until_stockout.toFixed(0)} dias`} accent={forecast.stockout_risk} />
            <Insight title="Compra sugerida" value={`${forecast.suggested_purchase} un`} hint={formatBRL(forecast.suggested_purchase * forecast.cost) + " investidos"} accent={forecast.suggested_purchase > 0} />
            <Insight title="Estoque parado" value={forecast.is_idle ? "SIM" : "Não"} hint={forecast.is_idle ? `Liquidação recomendada` : "Giro saudável"} />
          </div>
        </>
      )}
    </Page>
  );
}

const StatBlock = ({ icon: Icon, label, value, accent }) => (
  <div className={`p-5 rounded-xl border bg-card ${accent ? "border-primary/40" : "border-border"}`}>
    <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">{label}{Icon && <Icon className="w-4 h-4" />}</div>
    <div className={`font-display text-2xl font-bold mt-2 ${accent ? "text-primary" : ""}`}>{value}</div>
  </div>
);

const Insight = ({ title, value, hint, accent }) => (
  <div className={`p-5 rounded-xl border bg-card ${accent ? "border-primary/40 glow-coral" : "border-border"}`}>
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{title}</div>
    <div className={`font-display text-2xl font-bold mt-2 ${accent ? "text-primary" : ""}`}>{value}</div>
    <div className="text-xs text-muted-foreground mt-1">{hint}</div>
  </div>
);
